// frontend/src/pages/PoliticaPrivacidade.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaPrivacidade() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-privacidade.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-privacidade.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política de Privacidade</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DE PRIVACIDADE</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                O Brechó Vizinho valoriza a privacidade e a proteção dos dados pessoais de seus usuários.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Esta Política de Privacidade explica como coletamos, utilizamos, armazenamos, compartilhamos e protegemos 
                os dados pessoais tratados por meio da plataforma, em conformidade com a Lei Geral de Proteção de Dados 
                Pessoais (LGPD – Lei nº 13.709/2018).
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ao utilizar a plataforma, o usuário declara estar ciente das práticas descritas nesta Política.
              </p>

              {/* 1. QUEM SOMOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. QUEM SOMOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Brechó Vizinho é uma plataforma digital comunitária destinada à divulgação de anúncios, doações, 
                  oportunidades e interação entre moradores de comunidades participantes.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Para fins da LGPD, o Brechó Vizinho atua como Controlador dos dados pessoais tratados por meio da 
                  plataforma.
                </p>
              </div>

              {/* 2. DADOS PESSOAIS COLETADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. DADOS PESSOAIS COLETADOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Poderemos coletar as seguintes categorias de dados:
                </p>
                
                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Dados de Identificação</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Nome</li>
                  <li>Sobrenome</li>
                  <li>Apelido (nickname), quando utilizado</li>
                  <li>E-mail</li>
                  <li>Número de telefone</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Dados Residenciais</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>CEP</li>
                  <li>Condomínio ou comunidade</li>
                  <li>Endereço</li>
                  <li>Número da residência</li>
                  <li>Complemento</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Dados de Perfil</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Foto de perfil</li>
                  <li>Informações voluntariamente fornecidas pelo usuário</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Dados de Utilização</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Data e hora de acesso</li>
                  <li>Endereço IP</li>
                  <li>Tipo de dispositivo</li>
                  <li>Navegador utilizado</li>
                  <li>Registros de navegação na plataforma</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Dados de Consentimento</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Aceitação dos Termos de Uso</li>
                  <li>Aceitação da Política de Privacidade</li>
                  <li>Autorizações concedidas pelo usuário</li>
                </ul>
              </div>

              {/* 3. FINALIDADES DO TRATAMENTO DOS DADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. FINALIDADES DO TRATAMENTO DOS DADOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os dados pessoais poderão ser utilizados para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Criar e administrar contas de usuários;</li>
                  <li>Validar o vínculo do usuário com determinada comunidade;</li>
                  <li>Permitir a utilização dos recursos da plataforma;</li>
                  <li>Exibir anúncios e conteúdos publicados;</li>
                  <li>Garantir a segurança da comunidade;</li>
                  <li>Prevenir fraudes e acessos indevidos;</li>
                  <li>Responder solicitações de suporte;</li>
                  <li>Cumprir obrigações legais e regulatórias;</li>
                  <li>Enviar comunicações operacionais relacionadas à plataforma;</li>
                  <li>Enviar comunicações promocionais quando houver consentimento do usuário.</li>
                </ul>
              </div>

              {/* 4. BASES LEGAIS UTILIZADAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. BASES LEGAIS UTILIZADAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O tratamento dos dados poderá ocorrer com fundamento nas seguintes bases legais previstas na LGPD:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Execução de contrato;</li>
                  <li>Cumprimento de obrigação legal ou regulatória;</li>
                  <li>Exercício regular de direitos;</li>
                  <li>Legítimo interesse;</li>
                  <li>Consentimento do titular.</li>
                </ul>
              </div>

              {/* 5. COMPARTILHAMENTO DE DADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. COMPARTILHAMENTO DE DADOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho não comercializa dados pessoais.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os dados poderão ser compartilhados apenas quando necessário para:
                </p>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Validação de Cadastro</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Proprietário previamente validado;</li>
                  <li>Síndico;</li>
                  <li>Administração do condomínio;</li>
                  <li>Associação de moradores;</li>
                  <li>Conselho gestor da comunidade;</li>
                  <li>Administradores autorizados da plataforma.</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Prestadores de Serviços</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Empresas de hospedagem;</li>
                  <li>Ferramentas de tecnologia;</li>
                  <li>Serviços de autenticação;</li>
                  <li>Ferramentas de comunicação.</li>
                </ul>

                <p className="text-gray-600 leading-relaxed mt-3 font-semibold">Autoridades Públicas</p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Quando exigido por lei ou determinação judicial.</li>
                </ul>

                <p className="text-gray-600 leading-relaxed">
                  O compartilhamento ocorrerá sempre de forma limitada ao mínimo necessário para cada finalidade.
                </p>
              </div>

              {/* 6. PUBLICIDADE E CONTEÚDO PATROCINADO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. PUBLICIDADE E CONTEÚDO PATROCINADO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá exibir conteúdos patrocinados e anúncios publicitários.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os dados pessoais dos usuários não serão vendidos a anunciantes.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A segmentação de conteúdos promocionais poderá ocorrer com base em informações gerais da comunidade 
                  ou região do usuário, respeitando os princípios da LGPD.
                </p>
              </div>

              {/* 7. ARMAZENAMENTO E SEGURANÇA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. ARMAZENAMENTO E SEGURANÇA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho adota medidas técnicas e organizacionais razoáveis para proteger os dados pessoais contra:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Acesso não autorizado;</li>
                  <li>Perda;</li>
                  <li>Alteração indevida;</li>
                  <li>Divulgação indevida;</li>
                  <li>Uso inadequado.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Apesar dos esforços de segurança, nenhum sistema é totalmente imune a riscos.
                </p>
              </div>

              {/* 8. PRAZO DE RETENÇÃO DOS DADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. PRAZO DE RETENÇÃO DOS DADOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os dados pessoais serão armazenados pelo tempo necessário para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Cumprimento das finalidades descritas nesta Política;</li>
                  <li>Atendimento de obrigações legais;</li>
                  <li>Exercício regular de direitos;</li>
                  <li>Preservação de evidências em caso de disputas ou auditorias.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Quando possível, os dados serão eliminados ou anonimizados.
                </p>
              </div>

              {/* 9. DIREITOS DO TITULAR DOS DADOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. DIREITOS DO TITULAR DOS DADOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Nos termos da LGPD, o usuário poderá solicitar:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Confirmação da existência de tratamento;</li>
                  <li>Acesso aos dados pessoais;</li>
                  <li>Correção de dados incompletos ou desatualizados;</li>
                  <li>Anonimização, bloqueio ou eliminação de dados;</li>
                  <li>Portabilidade dos dados;</li>
                  <li>Informações sobre compartilhamento de dados;</li>
                  <li>Revogação do consentimento;</li>
                  <li>Eliminação de dados tratados com base no consentimento.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  As solicitações poderão ser realizadas pelos canais oficiais da plataforma.
                </p>
              </div>

              {/* 10. EXCLUSÃO DE CONTA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. EXCLUSÃO DE CONTA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário poderá solicitar a exclusão de sua conta a qualquer momento.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Algumas informações poderão ser mantidas quando necessário para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Cumprimento de obrigação legal;</li>
                  <li>Exercício regular de direitos;</li>
                  <li>Prevenção de fraudes;</li>
                  <li>Atendimento a determinações judiciais ou administrativas.</li>
                </ul>
              </div>

              {/* 11. COOKIES E TECNOLOGIAS SEMELHANTES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. COOKIES E TECNOLOGIAS SEMELHANTES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma utiliza cookies e tecnologias semelhantes para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Funcionamento adequado do sistema;</li>
                  <li>Segurança;</li>
                  <li>Estatísticas de utilização;</li>
                  <li>Melhoria da experiência do usuário.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário poderá gerenciar suas preferências de cookies por meio das opções disponibilizadas na plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Informações adicionais estão disponíveis na Política de Cookies.
                </p>
              </div>

              {/* 12. COMUNICAÇÕES E MARKETING */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. COMUNICAÇÕES E MARKETING</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário poderá optar por receber:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Novidades da plataforma;</li>
                  <li>Informações sobre funcionalidades;</li>
                  <li>Promoções;</li>
                  <li>Conteúdos de interesse da comunidade.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O consentimento poderá ser revogado a qualquer momento.
                </p>
              </div>

              {/* 13. MENORES DE IDADE */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. MENORES DE IDADE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma destina-se exclusivamente a pessoas com 18 (dezoito) anos ou mais.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho não coleta intencionalmente dados pessoais de menores de idade.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Caso seja identificado cadastro realizado por menor de idade, a conta poderá ser suspensa ou removida e os 
                  dados poderão ser excluídos, observadas as obrigações legais aplicáveis.
                </p>
              </div>

              {/* 14. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">14. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A versão mais recente estará sempre disponível na plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Quando aplicável, os usuários serão informados sobre alterações relevantes.
                </p>
              </div>

              {/* 15. CONTATO E SOLICITAÇÕES LGPD */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">15. CONTATO E SOLICITAÇÕES LGPD</h2>
                <p className="text-gray-600 leading-relaxed">
                  Dúvidas, solicitações ou exercícios de direitos relacionados à proteção de dados poderão ser encaminhados 
                  pelos canais oficiais disponibilizados pelo Brechó Vizinho.
                </p>
              </div>

              {/* 16. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">16. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed">
                  Esta Política de Privacidade será interpretada de acordo com a legislação brasileira, especialmente a Lei Geral 
                  de Proteção de Dados Pessoais (LGPD).
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  Responsável pelo Tratamento de Dados Pessoais (DPO): Gustavo Gonzalez<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span><br />
                  Canal LGPD: <span className="text-[#449879] font-medium">privacidade@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}